import {
    Block,
    CHILD_OBJECT_INVALID,
    type Inline,
    NODE_DATA_INVALID,
    PARENT_OBJECT_INVALID,
    Schema,
    type SchemaRule,
    Text,
} from '@gitbook/slate';
import { normalizeURLAnchor, validateURLAnchor } from '@gitbook/util-url';

import { BLOCK_HEADING_1, BLOCK_HEADING_2, BLOCK_HEADING_3 } from '../types';
import { validateTextAlignment } from '../utils';

const headingValidation: SchemaRule = {
    parent: { objects: ['document', 'block', 'fragment'] },
    nodes: [{ objects: ['inline', 'text'] }],
    data: {
        id: (val) =>
            (typeof val === 'string' && validateURLAnchor(val)) || typeof val === 'undefined',

        align: validateTextAlignment,

        /**
         * True if this block has been created as the page title when formatting
         * to Markdown.
         */
        isMarkdownPageTitle: (val) => typeof val === 'boolean' || typeof val === 'undefined',
    },
    normalize: Schema.createNormalize({
        [NODE_DATA_INVALID]: (change, context) => {
            const { node, key } = context;
            // @ts-expect-error
            const value = node.data.get(key);

            if (key === 'id' && typeof value === 'string') {
                // @ts-expect-error
                change.setDataByKey(node.key, [key], normalizeURLAnchor(value));
            } else {
                // @ts-expect-error
                change.removeDataByKey(node.key, [key]);
            }
        },
        [PARENT_OBJECT_INVALID]: (change, context) => {
            // @ts-expect-error
            change.unwrapNodeByKey(context.node.key, { normalize: false });
        },
        [CHILD_OBJECT_INVALID]: (change, context) => {
            // @ts-expect-error
            change.unwrapBlockByKey(context.node.key, {}, { normalize: false });
        },
    }),
};

/**
 * Schema to validate headings.
 */
export const schema = Schema.create({
    blocks: {
        [BLOCK_HEADING_1]: headingValidation,
        [BLOCK_HEADING_2]: headingValidation,
        [BLOCK_HEADING_3]: headingValidation,
    },
});

const HEADING_TYPES = [BLOCK_HEADING_1, BLOCK_HEADING_2, BLOCK_HEADING_3];

/**
 * Return true if a block is a heading.
 */
export function isHeading(block: Block | string): boolean {
    return HEADING_TYPES.includes(typeof block === 'string' ? block : block.type);
}

/**
 * Create a heading-1 with some text
 */
export function createHeading1(text: string | Array<Inline | Text> = '') {
    return Block.create({
        type: BLOCK_HEADING_1,
        nodes: typeof text === 'string' ? [Text.create(text)] : text,
    });
}
